import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpDown, Check, ChevronLeft, ChevronRight,
  Download, Edit3, FileText, Grid3X3, Heart, Images, Info,
  LayoutGrid, List, Loader2, Pen,
  Play, Search, Share2, Trash2, X,
} from "lucide-react";
import { deleteRecord, estimateStorage, getAll, putRecord } from "../lib/indexedDb";
import { exportPhotoBlob, shareBlob, blobToDataUrl } from "../lib/exportUtils";
import AnnotationEditor from "./AnnotationEditor";
import ReportBuilder    from "./ReportBuilder";

// ─── constants ────────────────────────────────────────────────────────────────

const BASE_TABS = [
  { id: "all",       label: "All"       },
  { id: "photos",    label: "Photos"    },
  { id: "videos",    label: "Videos"    },
  { id: "scans",     label: "Scans"     },
  { id: "favorites", label: "Favorites" },
];

const SORTS = ["Newest", "Oldest", "Name A–Z"];

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "";
  const d    = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7)   return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: diff > 365 ? "numeric" : undefined });
}

function fmtShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtBytes(b) {
  if (!b) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, v = b;
  while (v >= 1024 && i < 3) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${units[i]}`;
}

function groupByDate(rows) {
  const map = new Map();
  rows.forEach((item) => {
    const label = fmtDate(item.createdAt);
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(item);
  });
  return [...map.entries()];
}

// ─── BlobImage ────────────────────────────────────────────────────────────────

function BlobImage({ blob, alt, className, draggable }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!blob) { setUrl(""); return; }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  if (!url) return <div className="gl-img-placeholder" />;
  return <img src={url} alt={alt || ""} className={className} draggable={draggable ?? false} />;
}

function BlobVideo({ blob, className, controls = true, muted = false, autoPlay = false }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!blob) { setUrl(""); return; }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  if (!url) return <div className="gl-img-placeholder" />;
  return (
    <video src={url} className={className} controls={controls} muted={muted}
      autoPlay={autoPlay} playsInline preload="metadata" />
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

function EmptyState({ query, activeTab }) {
  const msgs = {
    favorites: { icon: <Heart size={52} strokeWidth={1.5} />,  title: "No favorites yet", sub: "Tap the heart on any photo to save it here." },
    scans:     { icon: <Images size={52} strokeWidth={1.5} />, title: "No scans yet",      sub: "Use the Scanner tab to capture documents, receipts, and QR codes." },
    photos:    { icon: <Images size={52} strokeWidth={1.5} />, title: "No photos yet",     sub: "Capture your first shot from the Camera tab." },
  };
  const q = query ? { icon: <Search size={52} strokeWidth={1.5} />, title: `No results for "${query}"`, sub: "Try a different search term." } : null;
  const { icon, title, sub } = q || msgs[activeTab] || { icon: <Images size={52} strokeWidth={1.5} />, title: "Nothing here yet", sub: "Capture photos or scan documents to fill your gallery." };
  return (
    <div className="gl-empty">
      <div className="gl-empty-icon">{icon}</div>
      <p className="gl-empty-title">{title}</p>
      <p className="gl-empty-sub">{sub}</p>
    </div>
  );
}

// ─── PhotoTile ────────────────────────────────────────────────────────────────

function PhotoTile({ item, viewMode, selecting, checked, onOpen, onEdit, onDelete, onFavToggle, onLongPress }) {
  const blob    = item.editedBlob || item.blob;
  const isVideo = item.type === "video";
  const pressTimer = useRef(null);

  const startPress = useCallback(() => {
    if (selecting) return;
    pressTimer.current = setTimeout(() => onLongPress?.(), 430);
  }, [selecting, onLongPress]);

  const endPress = useCallback(() => {
    clearTimeout(pressTimer.current);
    pressTimer.current = null;
  }, []);

  const handleClick = useCallback(() => { onOpen(item); }, [item, onOpen]);

  if (viewMode === "list") {
    return (
      <div
        className={`gl-list-row${checked ? " checked" : ""}${selecting ? " selecting" : ""}`}
        onPointerDown={startPress} onPointerUp={endPress} onPointerCancel={endPress}
        onClick={handleClick}
      >
        <div className="gl-list-thumb">
          {isVideo
            ? <BlobVideo blob={blob} className="gl-list-img" controls={false} muted />
            : <BlobImage blob={blob} alt={item.name} className="gl-list-img" />}
          {isVideo && !selecting && <span className="gl-play-badge"><Play size={10} fill="currentColor" /></span>}
          {selecting && (
            <div className={`gl-list-check${checked ? " on" : ""}`}>
              {checked && <Check size={11} />}
            </div>
          )}
        </div>
        <div className="gl-list-meta">
          <span className="gl-list-name">{item.name || item.title || "Untitled"}</span>
          <div className="gl-list-sub">
            <span className="gl-list-date">{fmtShort(item.createdAt)}</span>
            {item.type && <span className="gl-type-badge">{item.type}</span>}
            {item.favorite && <Heart size={11} fill="currentColor" className="gl-list-fav-icon" />}
          </div>
        </div>
        {!selecting && (
          <div className="gl-list-actions" onClick={(e) => e.stopPropagation()}>
            {!item.isScan && (
              <button className="gl-list-btn" onClick={() => onEdit(item)}>
                <Edit3 size={15} />
              </button>
            )}
            {blob && (
              <button className="gl-list-btn" onClick={() => exportPhotoBlob(blob, { format: "jpg", filename: (item.name || "lenspro").replace(/\W+/g, "-") })}>
                <Download size={15} />
              </button>
            )}
            <button className={`gl-list-btn${item.favorite ? " fav" : ""}`} onClick={() => onFavToggle(item)}>
              <Heart size={15} fill={item.favorite ? "currentColor" : "none"} />
            </button>
            <button className="gl-list-btn danger" onClick={() => onDelete(item)}>
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`gl-tile${checked ? " checked" : ""}${selecting ? " selecting" : ""}`}
      onPointerDown={startPress} onPointerUp={endPress} onPointerCancel={endPress}
      onClick={handleClick}
    >
      <div className="gl-tile-img-wrap">
        {isVideo
          ? <BlobVideo blob={blob} className="gl-tile-img" controls={false} muted />
          : <BlobImage blob={blob} alt={item.name} className="gl-tile-img" />}
        {isVideo && <span className="gl-play-badge"><Play size={14} fill="currentColor" /></span>}
        <div className="gl-tile-overlay" aria-hidden="true" />
        {!selecting && (
          <button
            className={`gl-tile-fav${item.favorite ? " on" : ""}`}
            onClick={(e) => { e.stopPropagation(); onFavToggle(item); }}
          >
            <Heart size={13} fill={item.favorite ? "currentColor" : "none"} />
          </button>
        )}
        {selecting && (
          <div className={`gl-tile-check${checked ? " on" : ""}`}>
            {checked && <Check size={12} />}
          </div>
        )}
        {item.type && <span className="gl-tile-badge">{item.type}</span>}
        {viewMode === "grid3" && (
          <div className="gl-tile-name-overlay">{item.name || item.title || ""}</div>
        )}
      </div>
      {viewMode === "grid2" && (
        <div className="gl-tile-info">
          <span className="gl-tile-name">{item.name || item.title || "Untitled"}</span>
          <span className="gl-tile-date">{fmtShort(item.createdAt)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

function Lightbox({ item, allItems, onClose, onEdit, onDelete, onFavToggle, onAnnotate }) {
  const startIdx = Math.max(0, allItems.findIndex((i) => i.id === item.id));
  const [idx,      setIdx]      = useState(startIdx);
  const [showInfo, setShowInfo] = useState(false);
  const [sharing,  setSharing]  = useState(false);
  const touchRef = useRef(null);

  const current  = allItems[idx] || item;
  const blob     = current?.editedBlob || current?.blob;
  const isVideo  = current?.type === "video";
  const canPrev  = idx > 0;
  const canNext  = idx < allItems.length - 1;

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowLeft"  && canPrev) setIdx((i) => i - 1);
      if (e.key === "ArrowRight" && canNext) setIdx((i) => i + 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [canPrev, canNext, onClose]);

  const handleTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchRef.current === null) return;
    const diff = touchRef.current - e.changedTouches[0].clientX;
    touchRef.current = null;
    if (Math.abs(diff) < 52) return;
    if (diff > 0 && canNext) setIdx((i) => i + 1);
    if (diff < 0 && canPrev) setIdx((i) => i - 1);
  };

  const handleShare = async () => {
    if (!blob) return;
    setSharing(true);
    try { await shareBlob(blob, current?.name || "Photo"); } catch (_) {}
    finally { setSharing(false); }
  };

  const handleDownload = () => {
    if (!blob) return;
    if (isVideo) {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href = url; a.download = `${(current?.name || "lenspro-video").replace(/\W+/g, "-")}.mp4`;
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } else {
      exportPhotoBlob(blob, { format: "jpg", filename: (current?.name || "lenspro").replace(/\W+/g, "-") });
    }
  };

  return (
    <div className="gl-lightbox" role="dialog" aria-modal="true">
      <header className="gl-lb-header safe-top">
        <button className="gl-lb-btn" onClick={onClose}><X size={20} /></button>
        <div className="gl-lb-title-wrap">
          <span className="gl-lb-name">{current?.name || current?.title || "Photo"}</span>
          <span className="gl-lb-counter">{idx + 1} / {allItems.length}</span>
        </div>
        <div className="gl-lb-header-actions">
          <button className={`gl-lb-btn${showInfo ? " on" : ""}`} onClick={() => { setShowInfo((v) => !v); setShowOcr(false); }}>
            <Info size={18} />
          </button>
          <button className="gl-lb-btn" onClick={handleShare} disabled={sharing}>
            {sharing ? <Loader2 size={18} className="spin" /> : <Share2 size={18} />}
          </button>
        </div>
      </header>

      <div
        className={`gl-lb-img-wrap${isVideo ? " gl-lb-video-wrap" : ""}`}
        onTouchStart={!isVideo ? handleTouchStart : undefined}
        onTouchEnd={!isVideo ? handleTouchEnd : undefined}
      >
        {blob && (isVideo ? (
          <BlobVideo key={current?.id} blob={blob} className="gl-lb-video" controls autoPlay={false} />
        ) : (
          <BlobImage key={current?.id} blob={blob} alt={current?.name} className="gl-lb-img" />
        ))}
        {canPrev && <button className="gl-lb-nav prev" onClick={() => setIdx((i) => i - 1)}><ChevronLeft size={24} /></button>}
        {canNext && <button className="gl-lb-nav next" onClick={() => setIdx((i) => i + 1)}><ChevronRight size={24} /></button>}
      </div>

      {/* Info panel */}
      {showInfo && (
        <div className="gl-lb-info">
          <div className="gl-lb-info-row"><span>Date</span><span>{current?.createdAt ? new Date(current.createdAt).toLocaleString() : "—"}</span></div>
          {current?.type   && <div className="gl-lb-info-row"><span>Type</span><span>{current.type}</span></div>}
          {current?.album  && <div className="gl-lb-info-row"><span>Album</span><span>{current.album}</span></div>}
          {current?.text && (
            <div className="gl-lb-info-row gl-lb-info-text-row">
              <span>Content</span>
              <span className="gl-lb-info-text">{current.text.slice(0, 160)}{current.text.length > 160 ? "…" : ""}</span>
            </div>
          )}
        </div>
      )}

      <footer className="gl-lb-footer safe-bottom">
        <div className="gl-lb-action-row">
          {!current?.isScan && (
            <button className="gl-lb-icon-btn" onClick={() => { onEdit(current); onClose(); }} title={isVideo ? "Edit video" : "Edit"}>
              <Edit3 size={20} />
              <span>{isVideo ? "Video" : "Edit"}</span>
            </button>
          )}
          {!current?.isScan && !isVideo && (
            <button className="gl-lb-icon-btn" onClick={() => onAnnotate(current)} title="Annotate">
              <Pen size={20} />
              <span>Annotate</span>
            </button>
          )}
          <button
            className={`gl-lb-icon-btn gl-lb-icon-fav${current?.favorite ? " on" : ""}`}
            onClick={() => onFavToggle(current)}
            title={current?.favorite ? "Unfavourite" : "Favourite"}
          >
            <Heart size={20} fill={current?.favorite ? "currentColor" : "none"} />
            <span>{current?.favorite ? "Unfav" : "Fav"}</span>
          </button>
          {blob && (
            <button className="gl-lb-icon-btn gl-lb-icon-save" onClick={handleDownload} title="Save">
              <Download size={20} />
              <span>Save</span>
            </button>
          )}
          <button
            className="gl-lb-icon-btn gl-lb-icon-del"
            onClick={() => { onDelete(current); onClose(); }}
            title="Delete"
          >
            <Trash2 size={20} />
            <span>Delete</span>
          </button>
        </div>
      </footer>
    </div>
  );
}

// ─── Gallery ─────────────────────────────────────────────────────────────────

export default function Gallery({ onEdit, refreshKey }) {
  const [photos,       setPhotos]       = useState([]);
  const [scans,        setScans]        = useState([]);
  const [query,        setQuery]        = useState("");
  const [activeTab,    setActiveTab]    = useState("all");
  const [sort,         setSort]         = useState("Newest");
  const [viewMode,     setViewMode]     = useState("grid2");   // grid3 | grid2 | list | map
  const [lightboxItem, setLightboxItem] = useState(null);
  const [selecting,    setSelecting]    = useState(false);
  const [checked,      setChecked]      = useState(new Set());
  const [storageInfo,  setStorageInfo]  = useState(null);
  const [annotateItem, setAnnotateItem] = useState(null);
  const [showReport,   setShowReport]   = useState(false);
  const sortIdx = useRef(0);

  const load = useCallback(async () => {
    const [ph, sc] = await Promise.all([getAll("photos"), getAll("scans")]);
    setPhotos(ph);
    setScans(sc);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { estimateStorage().then(setStorageInfo).catch(() => {}); }, []);

  const combined = useMemo(
    () => [...photos, ...scans.map((s) => ({ ...s, isScan: true }))],
    [photos, scans]
  );

  const tabCounts = useMemo(() => ({
    all:       combined.length,
    photos:    photos.filter((p) => p.type !== "video").length,
    videos:    photos.filter((p) => p.type === "video").length,
    scans:     scans.length,
    favorites: combined.filter((i) => i.favorite).length,
  }), [combined, photos, scans]);

  const allRows = useMemo(() => {
    let rows = [...combined];
    if (activeTab === "photos")    rows = rows.filter((i) => !i.isScan && i.type !== "video");
    if (activeTab === "videos")    rows = rows.filter((i) => i.type === "video");
    if (activeTab === "scans")     rows = rows.filter((i) => i.isScan);
    if (activeTab === "favorites") rows = rows.filter((i) => i.favorite);
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter((i) =>
        `${i.name||i.title||""} ${i.type||""} ${i.text||""}`.toLowerCase().includes(q)
      );
    }
    if (sort === "Oldest")        rows.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    else if (sort === "Name A–Z") rows.sort((a, b) => (a.name||a.title||"").localeCompare(b.name||b.title||""));
    else                          rows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return rows;
  }, [combined, activeTab, query, sort]);

  const grouped = useMemo(() => groupByDate(allRows), [allRows]);

  // ── actions ────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (item) => {
    await deleteRecord(item.isScan ? "scans" : "photos", item.id);
    load();
  }, [load]);

  const handleFavToggle = useCallback(async (item) => {
    const updated = { ...item, favorite: !item.favorite, updatedAt: new Date().toISOString() };
    await putRecord(item.isScan ? "scans" : "photos", updated);
    if (item.isScan) setScans((p) => p.map((s) => s.id === item.id ? updated : s));
    else             setPhotos((p) => p.map((ph) => ph.id === item.id ? updated : ph));
  }, []);

  const toggleChecked = useCallback((item) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    const targets = allRows.filter((i) => checked.has(i.id));
    await Promise.all(targets.map((i) => deleteRecord(i.isScan ? "scans" : "photos", i.id)));
    setChecked(new Set()); setSelecting(false); load();
  }, [allRows, checked, load]);

  const handleBulkFav = useCallback(async () => {
    const targets = allRows.filter((i) => checked.has(i.id));
    await Promise.all(targets.map((i) => putRecord(i.isScan ? "scans" : "photos", { ...i, favorite: true })));
    load();
  }, [allRows, checked, load]);

  const handleAnnotateSave = useCallback(async (newBlob) => {
    if (!annotateItem) return;
    const updated = { ...annotateItem, editedBlob: newBlob, updatedAt: new Date().toISOString() };
    await putRecord(annotateItem.isScan ? "scans" : "photos", updated);
    setAnnotateItem(null);
    load();
  }, [annotateItem, load]);

  const cycleSort    = () => { sortIdx.current = (sortIdx.current + 1) % SORTS.length; setSort(SORTS[sortIdx.current]); };
  const toggleSelect = () => { setSelecting((v) => !v); setChecked(new Set()); };

  const checkedPhotos = useMemo(
    () => allRows.filter((i) => checked.has(i.id) && !i.isScan && i.type !== "video"),
    [allRows, checked]
  );

  const totalSize = photos.length + scans.length;

  // ── Annotation editor fullscreen ───────────────────────────────────────────
  if (annotateItem) {
    return (
      <AnnotationEditor
        blob={annotateItem.editedBlob || annotateItem.blob}
        onSave={handleAnnotateSave}
        onCancel={() => setAnnotateItem(null)}
      />
    );
  }

  return (
    <section className="panel-page gallery-page">

      {/* ── header ────────────────────────────────────────────────────── */}
      <header className="gl-header safe-top">
        <div className="gl-header-left">
          <h1 className="gl-title">Gallery</h1>
          <span className="gl-subtitle">
            {totalSize} item{totalSize !== 1 ? "s" : ""}
            {storageInfo?.usage ? ` · ${fmtBytes(storageInfo.usage)} used` : ""}
          </span>
        </div>
        <button className={`gl-select-toggle${selecting ? " on" : ""}`} onClick={toggleSelect}>
          {selecting ? <><X size={14} /> Cancel</> : <><Check size={14} /> Select</>}
        </button>
      </header>

      {/* ── filter tabs ───────────────────────────────────────────────── */}
      <div className="gl-filter-tabs" role="tablist">
        {BASE_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`gl-filter-tab${activeTab === tab.id ? " on" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tabCounts[tab.id] > 0 && <span className="gl-tab-count">{tabCounts[tab.id]}</span>}
          </button>
        ))}
      </div>


      {/* ── toolbar ───────────────────────────────────────────────────── */}
      <div className="gl-toolbar">
        <div className="gl-search-wrap">
          <Search size={15} className="gl-search-icon" />
          <input
            className="gl-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search photos, scans…"
          />
          {query && <button className="gl-search-clear" onClick={() => setQuery("")}><X size={13} /></button>}
        </div>
        <div className="gl-toolbar-right">
          <button className="gl-sort-btn" onClick={cycleSort} title={`Sort: ${sort}`}>
            <ArrowUpDown size={13} /> {sort}
          </button>
          <div className="gl-view-toggle">
            <button className={viewMode === "grid3" ? "on" : ""} onClick={() => setViewMode("grid3")} title="Dense grid">
              <Grid3X3 size={14} />
            </button>
            <button className={viewMode === "grid2" ? "on" : ""} onClick={() => setViewMode("grid2")} title="Grid">
              <LayoutGrid size={14} />
            </button>
            <button className={viewMode === "list" ? "on" : ""} onClick={() => setViewMode("list")} title="List">
              <List size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── bulk action bar ───────────────────────────────────────────── */}
      {selecting && (
        <div className="gl-bulk-bar">
          <span className="gl-bulk-count">
            {checked.size > 0 ? `${checked.size} selected` : "Tap to select"}
          </span>
          <div className="gl-bulk-actions">
            <button onClick={() => setChecked(new Set(allRows.map((i) => i.id)))}>All</button>
            <button onClick={() => setChecked(new Set())}>None</button>
            {checked.size > 0 && (
              <>
                <button onClick={handleBulkFav}>
                  <Heart size={13} /> Fav
                </button>
                {checkedPhotos.length > 0 && (
                  <button
                    className="gl-bulk-report-btn"
                    onClick={() => setShowReport(true)}
                  >
                    <FileText size={13} /> Report
                  </button>
                )}
                <button className="danger" onClick={handleBulkDelete}>
                  <Trash2 size={13} /> Delete {checked.size}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── content ───────────────────────────────────────────────────── */}
      {allRows.length === 0 ? (
        <EmptyState query={query} activeTab={activeTab} />
      ) : (
        <div className={`gl-grid ${viewMode}`}>
          {grouped.map(([label, items]) => (
            <Fragment key={label}>
              <div className="gl-date-header">
                <span className="gl-date-label">{label}</span>
                <span className="gl-date-count">{items.length}</span>
              </div>
              {items.map((item) => (
                <PhotoTile
                  key={item.id}
                  item={item}
                  viewMode={viewMode}
                  selecting={selecting}
                  checked={checked.has(item.id)}
                  onOpen={(i) => {
                    if (selecting) { toggleChecked(i); return; }
                    setLightboxItem(i);
                  }}
                  onEdit={(i) => !i.isScan && onEdit(i)}
                  onDelete={handleDelete}
                  onFavToggle={handleFavToggle}
                  onLongPress={() => { setSelecting(true); toggleChecked(item); }}
                />
              ))}
            </Fragment>
          ))}
        </div>
      )}

      {/* ── lightbox ──────────────────────────────────────────────────── */}
      {lightboxItem && (
        <Lightbox
          item={lightboxItem}
          allItems={allRows}
          onClose={() => setLightboxItem(null)}
          onEdit={(i) => { onEdit(i); setLightboxItem(null); }}
          onDelete={async (i) => { await handleDelete(i); setLightboxItem(null); }}
          onFavToggle={handleFavToggle}
          onAnnotate={(i) => { setAnnotateItem(i); setLightboxItem(null); }}
        />
      )}

      {/* ── report builder ────────────────────────────────────────────── */}
      {showReport && checkedPhotos.length > 0 && (
        <ReportBuilder photos={checkedPhotos} onClose={() => setShowReport(false)} />
      )}

    </section>
  );
}
